import { Link } from "react-router-dom";
import {
  ActivityIcon,
  BellIcon,
  CheckCircle2Icon,
  VideoIcon,
} from "lucide-react";

import { useAlertEvents } from "@/api/alerts";
import { useDashboardSummary } from "@/api/dashboard";
import { useEntities, useEntityHealth } from "@/api/entities";
import { useRecordingConfigs } from "@/api/recordings";
import { useStatusFeed } from "@/api/status_feed";
import { DashboardRecordingWidget } from "@/components/DashboardRecordingWidget";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/utils";

function healthColor(pct: number): string {
  if (pct > 80) return "bg-emerald-500";
  if (pct >= 50) return "bg-amber-500";
  return "bg-red-500";
}

function healthLabel(pct: number): "online" | "warning" | "error" {
  if (pct > 80) return "online";
  if (pct >= 50) return "warning";
  return "error";
}

interface KpiCardProps {
  label: string;
  value: number | string;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  accent?: "default" | "good" | "warn" | "bad";
}

function KpiCard({ label, value, hint, icon: Icon, accent = "default" }: KpiCardProps) {
  const accentClass: Record<NonNullable<KpiCardProps["accent"]>, string> = {
    default: "text-foreground",
    good: "text-emerald-600",
    warn: "text-amber-600",
    bad: "text-red-600",
  };
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
          <p className={cn("text-3xl font-semibold", accentClass[accent])}>
            {value}
          </p>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        <Icon className={cn("h-8 w-8 opacity-30")} />
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const { data, isLoading, isError } = useDashboardSummary();
  const { connected } = useStatusFeed();
  // T3b — how many live cameras have NO recording config. Those are the ones
  // the compliance scorer charges 0% to without ever having been asked to
  // record, so the tile's hint has to be able to say how many there are.
  const recConfigs = useRecordingConfigs();
  const configuredCameraIds = new Set(
    (recConfigs.data?.items ?? []).filter((c) => c.enabled).map((c) => c.camera_id),
  );
  const totalCameras = data?.cameras?.total ?? data?.total_cameras ?? 0;
  // Only claim a count when the config list actually loaded — otherwise a
  // failed configs fetch would assert "15 cameras have no recording
  // configured", which is the same false-fact class this tranche is closing.
  const unconfiguredCameras = recConfigs.isSuccess
    ? Math.max(0, totalCameras - configuredCameraIds.size)
    : 0;
  // §67 — the backend now distinguishes "no score" from "0%". Prefer its own
  // counts (authoritative, tenant-scoped) and fall back to the config list.
  const complianceScore = data?.compliance?.avg_score_today ?? null;
  const scored = data?.compliance?.cameras_scored ?? 0;
  const unconfiguredFromApi =
    data?.compliance?.cameras_unconfigured ?? unconfiguredCameras;
  const complianceHint =
    complianceScore === null
      ? unconfiguredFromApi > 0
        ? `no cameras configured to record — ${unconfiguredFromApi} of ${totalCameras} unconfigured`
        : "no compliance data recorded for today yet"
      : `${scored} camera${scored === 1 ? "" : "s"} scored · `
        + `${data?.compliance?.cameras_below_threshold ?? 0} below threshold`
        + (unconfiguredFromApi > 0
          ? ` · ${unconfiguredFromApi} not configured (excluded)`
          : "");

  if (isLoading) {
    return <p className="py-10 text-center text-sm">Loading…</p>;
  }
  if (isError || !data) {
    return (
      <p className="py-10 text-center text-sm text-destructive">
        Could not load dashboard.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Tenant-wide camera + branch health · {connected ? (
              <span className="text-emerald-600">● live</span>
            ) : (
              <span>○ connecting</span>
            )}
          </p>
        </div>
      </div>

      {/* S12 KPI strip — uses fielded blocks when present, falls back to
          flat fields for backward compatibility. */}
      <div className="grid gap-3 md:grid-cols-4">
        <KpiCard
          label="Cameras Online"
          value={`${data.cameras?.online ?? data.cameras_online} / ${data.cameras?.total ?? data.total_cameras}`}
          icon={VideoIcon}
          accent={(data.cameras?.online ?? data.cameras_online) > 0 ? "good" : "warn"}
          hint={`${data.cameras?.offline ?? data.cameras_offline} offline`}
        />
        <KpiCard
          label="Recording Active"
          value={`${data.cameras?.recording ?? 0} / ${data.cameras?.total ?? data.total_cameras}`}
          icon={ActivityIcon}
          accent="good"
        />
        {/* T3a — LABELLED, NOT RECONCILED. This tile and Health's "Active
            Alerts" tile read DIFFERENT TABLES by design:

              here          alert_events   — the user-configurable rules engine
              Health tab    health_alerts  — the health monitor

            They measured 1 open / 0 critical here against 44 / 40 there on the
            same fleet at the same moment, and BOTH were right. Collapsing them
            would destroy the distinction, so the label now names its source.
            See docs/audit/UI-AUDIT-TRANCHES.md T3a. */}
        <KpiCard
          label="Alert Events (open)"
          value={data.alerts?.open ?? 0}
          icon={BellIcon}
          accent={(data.alerts?.critical ?? 0) > 0 ? "bad" : "default"}
          hint={`from alert rules · ${data.alerts?.critical ?? 0} critical · ${data.alerts?.high ?? 0} high`}
        />
        {/* T3b — the number MEANS "recording compliance", and on this fleet it
            reads 0% for a reason that is NOT a compliance failure.

            `_expected_seconds_for_date` defaults to 86400 when a camera has NO
            recording_config at all, so an UNCONFIGURED camera is scored as
            though 24 h of recording were expected and it delivered none. The
            label must not let that be read as "the recorder failed" — it is
            "no recorder was ever asked for". Backlog §67 ranks the underlying
            scorer as a REPORTING defect with audit consequences.

            Health's "Camera Uptime" tile is a different measurement again
            (uptime, not recording), which is why it reads ~56% beside this 0%. */}
        {/* §67 — THREE STATES, because 0% used to mean all three.
              scored     a real recording-compliance figure
              —          no camera is configured to record, so there is
                         nothing to be compliant WITH. Not a failure.
              no data     configured, but today's sweep has not run yet.
            An auditor must not be able to read "not configured" as "failed to
            record". The backend now returns null rather than 0.0 and says how
            many cameras were asked to record at all. */}
        <KpiCard
          label="Recording Compliance (today)"
          value={complianceScore === null ? "—" : `${complianceScore}%`}
          icon={CheckCircle2Icon}
          accent={
            complianceScore === null
              ? "default"
              : complianceScore >= 80 ? "good" : "warn"
          }
          hint={complianceHint}
        />
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <DashboardRecordingWidget />
      </div>

      <EntityCards />

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Sites grid */}
        <div className="space-y-3 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Sites</h2>
            <Button asChild variant="outline" size="sm">
              <Link to="/sites">View all</Link>
            </Button>
          </div>

          {data.sites.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No sites yet. Create your first branch under{" "}
                <Link to="/sites" className="underline">
                  Sites
                </Link>
                .
              </CardContent>
            </Card>
          )}

          <div className="grid gap-3 md:grid-cols-2">
            {data.sites.map((s) => {
              const c = s.camera_counts;
              return (
                <Card key={s.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{s.name}</CardTitle>
                        <p className="text-xs text-muted-foreground">
                          {s.city ?? "—"}
                        </p>
                      </div>
                      <Badge variant={healthLabel(s.health_pct)}>
                        {Math.round(s.health_pct)}%
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="h-2 w-full rounded-full bg-muted">
                      <div
                        className={cn(
                          "h-2 rounded-full transition-all",
                          healthColor(s.health_pct),
                        )}
                        style={{ width: `${Math.max(2, s.health_pct)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {c.online}/{c.total} online
                      </span>
                      <Button asChild variant="ghost" size="sm">
                        <Link to={`/sites/${s.id}`}>View site →</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Active alerts */}
        <Card className="lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <BellIcon className="h-4 w-4" />
              Active alerts
            </CardTitle>
            <Button asChild size="sm" variant="outline">
              <Link to="/alerts">View all</Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            <ActiveAlertsPanel />
            {data.recent_alerts.length > 0 && (
              <details className="pt-2 text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Recent audit activity
                </summary>
                <div className="mt-2 space-y-1">
                  {data.recent_alerts.map((a) => (
                    <div
                      key={a.id}
                      className="rounded border bg-muted/30 p-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[11px]">{a.action}</span>
                        <span className="text-muted-foreground">
                          {formatDate(a.created_at)}
                        </span>
                      </div>
                      {a.actor_email && (
                        <div className="text-muted-foreground">{a.actor_email}</div>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </CardContent>
        </Card>
      </div>

      {/* S12 — Health overview + Quick actions */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Health overview (today)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <HealthBar label="Average uptime" pct={data.health?.overall_uptime_pct ?? 0} />
            <HealthBar label="Average recording" pct={data.health?.overall_recording_pct ?? 0} />
            <p className="text-xs text-muted-foreground">
              Storage used: {data.recordings?.total_size_gb ?? 0} GB ·
              Recording gaps today: {data.recordings?.gaps_today ?? 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quick actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button asChild className="w-full" variant="outline"><Link to="/cameras/add">Add Camera</Link></Button>
            <Button asChild className="w-full" variant="outline"><Link to="/reports">Generate Report</Link></Button>
            <Button asChild className="w-full" variant="outline"><Link to="/health">Health Dashboard</Link></Button>
            <Button asChild className="w-full" variant="outline"><Link to="/evidence">Evidence Center</Link></Button>
          </CardContent>
        </Card>
      </div>

      {/* S12 — Recent activity rail */}
      {(data.recent_activity ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {(data.recent_activity ?? []).map((a) => (
              <div key={a.id} className="flex justify-between rounded border bg-muted/20 p-2 text-xs">
                <span className="font-mono">{a.action}</span>
                <span className="text-muted-foreground">{a.actor_email ?? "system"}</span>
                <span className="text-muted-foreground">{(a.created_at as any)?.local ?? ""}</span>
              </div>
            ))}
            <Button asChild size="sm" variant="ghost" className="mt-2">
              <Link to="/audit">View audit log →</Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function HealthBar({ label, pct }: { label: string; pct: number }) {
  const colour =
    pct > 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
  return (
    <div>
      <div className="mb-1 flex justify-between text-xs">
        <span>{label}</span>
        <span className="font-mono">{pct.toFixed(1)}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted">
        <div
          className={cn("h-2 rounded-full", colour)}
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
    </div>
  );
}


function ActiveAlertsPanel() {
  const { data } = useAlertEvents({ status: "open", limit: 10 });
  if (!data) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (data.items.length === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-emerald-600">
        <CheckCircle2Icon className="h-4 w-4" />
        No open alerts — all clear.
      </p>
    );
  }
  return (
    <div className="space-y-1">
      {data.items.map((evt) => {
        const dot =
          evt.severity === "critical"
            ? "bg-red-500"
            : evt.severity === "warning"
              ? "bg-amber-500"
              : "bg-blue-500";
        return (
          <Link
            key={evt.id}
            to="/alerts"
            className="flex items-center gap-2 rounded border bg-muted/30 p-2 text-xs hover:bg-muted"
          >
            <span className={cn("h-2 w-2 rounded-full", dot)} />
            <span className="flex-1 truncate">{evt.title}</span>
            <span className="text-muted-foreground">
              {formatDate(evt.created_at)}
            </span>
          </Link>
        );
      })}
    </div>
  );
}


// S12 — Entity rollup cards on the dashboard. Shows one card per
// top-level (parent_id IS NULL) entity with its camera-health rollup.
function EntityCards() {
  const { data: roots = [] } = useEntities();
  if (roots.length === 0) return null;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Entities</h2>
        <Button asChild variant="outline" size="sm">
          <Link to="/entities">Browse hierarchy</Link>
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {roots.map((r) => (
          <EntityCard key={r.id} entityId={r.id} name={r.name} />
        ))}
      </div>
    </div>
  );
}

function EntityCard({ entityId, name }: { entityId: string; name: string }) {
  const { data: health } = useEntityHealth(entityId);
  const total = health?.cameras_total ?? 0;
  const online = health?.cameras_online ?? 0;
  const offline = health?.cameras_offline ?? 0;
  const pct = total ? Math.round((online / total) * 100) : 0;
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <Link
          to={`/entities?selected=${entityId}`}
          className="text-base font-semibold hover:underline"
        >
          {name}
        </Link>
        <p className="text-xs text-muted-foreground">
          {total} camera{total === 1 ? "" : "s"} in subtree
        </p>
        <div className="h-2 w-full rounded-full bg-muted">
          <div
            className={cn("h-2 rounded-full", healthColor(pct))}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-emerald-600">{online} online</span>
          <span className="text-red-600">{offline} offline</span>
        </div>
      </CardContent>
    </Card>
  );
}
